// 货币和汇率服务
// 支持多货币显示和实时汇率换算

import { currencyStorage, getCurrencyServiceLanguage, getCurrencyServiceConfigValue } from './adapters';

// 汇率缓存持续时间（24小时）
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24小时缓存

export interface Currency {
  code: string;
  name: string;
  symbol: string;
  flag: string;
  decimals: number;
}

export interface ExchangeRate {
  from: string;
  to: string;
  rate: number;
  timestamp: number;
}

// 支持的货币列表
export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: 'CNY', name: '人民币', symbol: '¥', flag: '🇨🇳', decimals: 2 },
  { code: 'USD', name: '美元', symbol: '$', flag: '🇺🇸', decimals: 2 },
  { code: 'EUR', name: '欧元', symbol: '€', flag: '🇪🇺', decimals: 2 },
  { code: 'GBP', name: '英镑', symbol: '£', flag: '🇬🇧', decimals: 2 },
  { code: 'JPY', name: '日元', symbol: '¥', flag: '🇯🇵', decimals: 0 },
  { code: 'HKD', name: '港币', symbol: 'HK$', flag: '🇭🇰', decimals: 2 },
  { code: 'TWD', name: '新台币', symbol: 'NT$', flag: '🇹🇼', decimals: 2 },
  { code: 'SGD', name: '新加坡元', symbol: 'S$', flag: '🇸🇬', decimals: 2 },
  { code: 'AUD', name: '澳元', symbol: 'A$', flag: '🇦🇺', decimals: 2 },
  { code: 'CAD', name: '加元', symbol: 'C$', flag: '🇨🇦', decimals: 2 },
  { code: 'KRW', name: '韩元', symbol: '₩', flag: '🇰🇷', decimals: 0 },
  { code: 'THB', name: '泰铢', symbol: '฿', flag: '🇹🇭', decimals: 2 },
  { code: 'MYR', name: '马来西亚元', symbol: 'RM', flag: '🇲🇾', decimals: 2 },
  { code: 'VND', name: '越南盾', symbol: '₫', flag: '🇻🇳', decimals: 0 },
];

// 常用货币（用于快速选择）
export const POPULAR_CURRENCIES = ['CNY', 'USD', 'EUR', 'HKD', 'JPY'];

// 本地存储的汇率缓存键
const EXCHANGE_RATE_CACHE_KEY = 'exchange_rates_cache';

export class CurrencyService {
  private rateCache: Map<string, ExchangeRate> = new Map();
  private initializationPromise: Promise<void> | null = null;
  private isInitialized = false;
  private appLanguage?: string;

  constructor() {
    // 自动初始化缓存
    this.initializationPromise = this.initializeCache();
    // 尝试读取应用语言并缓存（非阻塞）
    try {
      getCurrencyServiceLanguage()
        .then((lang) => {
          if (lang) this.appLanguage = lang;
        })
        .catch(() => {});
    } catch (e) {
      // ignore
    }
  }

  private getExchangeRateApiKey(): string {
    return getCurrencyServiceConfigValue('EXCHANGE_RATE_API_KEY') || '';
  }

  // 初始化汇率缓存
  private async initializeCache(): Promise<void> {
    try {
      console.log('🚀 开始初始化汇率缓存...');

      // 1. 先尝试从本地存储加载缓存
      await this.loadCacheFromStorage();

      // 2. 预加载常用汇率组合
      await this.preloadCommonRates();

      this.isInitialized = true;
      console.log('✅ 汇率缓存初始化完成');
    } catch (error) {
      console.error('❌ 汇率缓存初始化失败:', error);
      this.isInitialized = true; // 即使失败也标记为已初始化，避免阻塞应用
    }
  }

  // 从本地存储加载缓存
  private async loadCacheFromStorage(): Promise<void> {
    try {
      const cachedData = await currencyStorage.getItem(EXCHANGE_RATE_CACHE_KEY);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        if (Array.isArray(parsed)) {
          for (const item of parsed) {
            // 只加载未过期的缓存（7天内）
            if (Date.now() - item.timestamp < CACHE_DURATION * 7) {
              const cacheKey = `${item.from}_${item.to}`;
              this.rateCache.set(cacheKey, item);
            }
          }
          console.log(`📱 从本地存储加载了 ${this.rateCache.size} 个汇率缓存`);
        }
      }
    } catch (error) {
      console.error('加载本地汇率缓存失败:', error);
    }
  }

  // 预加载常用汇率组合
  private async preloadCommonRates(): Promise<void> {
    const commonPairs = [
      // 以USD为基准的常用汇率
      { from: 'USD', to: 'CNY' },
      { from: 'USD', to: 'HKD' },
      { from: 'USD', to: 'TWD' },
      { from: 'USD', to: 'EUR' },
      { from: 'USD', to: 'JPY' },
      // 常用亚洲货币
      { from: 'CNY', to: 'HKD' },
      { from: 'CNY', to: 'TWD' },
      { from: 'TWD', to: 'HKD' },
    ];

    const promises = commonPairs.map(async ({ from, to }) => {
      const cacheKey = `${from}_${to}`;
      const existing = this.rateCache.get(cacheKey);

      // 只有在没有缓存或缓存过期时才请求
      if (!existing || Date.now() - existing.timestamp > CACHE_DURATION) {
        try {
          await this.getExchangeRate(from, to);
        } catch (error) {
          console.warn(`预加载汇率失败 ${from}->${to}:`, error);
        }
      }
    });

    await Promise.allSettled(promises);
  }

  // 确保服务已初始化
  async ensureInitialized(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  // 获取货币信息
  getCurrency(code: string): Currency | undefined {
    return SUPPORTED_CURRENCIES.find((currency) => currency.code === code);
  }

  // 获取所有支持的货币
  getAllCurrencies(): Currency[] {
    return SUPPORTED_CURRENCIES;
  }

  // 根据语言代码返回默认货币代码（用于界面预设）
  // 规则：
  // - 日文（ja*） -> JPY
  // - 韩文（ko*） -> KRW
  // - 繁体中文（zh-Hant / zh-TW / zh-HK / zh-hk / zh-tw） -> HKD
  // - 简体中文（zh, 不含 Hant/TW/HK） -> CNY
  // - 英语（en*） -> USD
  // - 其它语言默认 USD（可扩展）
  getDefaultCurrencyForLanguage(langCode?: string): string {
    if (!langCode) return 'USD';
    const l = langCode.toLowerCase();
    if (l.startsWith('ja')) return 'JPY';
    if (l.startsWith('ko')) return 'KRW';
    // 繁体中文判断（包括 zh-Hant, zh-tw, zh-hk, zh-hant-*）
    if (l.includes('hant') || l.startsWith('zh-tw') || l.startsWith('zh-hk')) return 'HKD';
    // 简体中文（zh 或 zh-cn）
    if (l.startsWith('zh')) return 'CNY';
    if (l.startsWith('en')) return 'USD';
    return 'USD';
  }

  // 获取常用货币
  getPopularCurrencies(): Currency[] {
    return SUPPORTED_CURRENCIES.filter((currency) => POPULAR_CURRENCIES.includes(currency.code));
  }

  // 根据语言或货币返回合适的 locale（用于 toLocaleString）
  private getLocaleForCurrency(currencyCode: string, lang?: string): string {
    const languageToUse = lang || this.appLanguage;
    if (languageToUse) {
      const l = languageToUse.toLowerCase();
      if (l.startsWith('en')) return 'en-US';
      if (l.startsWith('ja')) return 'ja-JP';
      if (l.startsWith('ko')) return 'ko-KR';
      if (l.includes('hant') || l.startsWith('zh-tw') || l.startsWith('zh-hk')) return 'zh-TW';
      if (l.startsWith('zh')) return 'zh-CN';
      // fallback to en-US for unknown language
      return 'en-US';
    }

    // Fallback by currency code
    switch ((currencyCode || '').toUpperCase()) {
      case 'USD':
        return 'en-US';
      case 'CNY':
        return 'zh-CN';
      case 'JPY':
        return 'ja-JP';
      case 'KRW':
        return 'ko-KR';
      case 'HKD':
        return 'zh-HK';
      case 'EUR':
        return 'de-DE';
      case 'GBP':
        return 'en-GB';
      default:
        return 'en-US';
    }
  }

  // 允许外部设置应用语言（当用户切换语言时调用）
  setAppLanguage(lang?: string) {
    this.appLanguage = lang;
  }

  // 获取当前缓存的应用语言（可能为 undefined）
  getAppLanguage(): string | undefined {
    return this.appLanguage;
  }

  // 格式化金额显示，支持可选的语言/locale 回退参数
  formatAmount(amount: number, currencyCode: string, showSymbol: boolean = true, localeOrLang?: string): string {
    const currency = this.getCurrency(currencyCode);
    if (!currency) return amount.toFixed(2);

    const locale = this.getLocaleForCurrency(currencyCode, localeOrLang);

    let formattedAmount: string;
    try {
      formattedAmount = amount.toLocaleString(locale, {
        minimumFractionDigits: currency.decimals,
        maximumFractionDigits: currency.decimals,
      });
    } catch (e) {
      // 如果指定 locale 不被支持，回退到默认格式
      formattedAmount = amount.toLocaleString(undefined, {
        minimumFractionDigits: currency.decimals,
        maximumFractionDigits: currency.decimals,
      });
    }

    return showSymbol ? `${currency.symbol}${formattedAmount}` : formattedAmount;
  }

  // 获取汇率（带缓存）
  async getExchangeRate(from: string, to: string): Promise<number> {
    if (from === to) return 1;

    const cacheKey = `${from}_${to}`;
    const cached = this.rateCache.get(cacheKey);

    // 检查缓存是否有效（24小时内）
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`💾 使用缓存汇率: ${from} -> ${to} = ${cached.rate}`);
      return cached.rate;
    }

    try {
      // 首选 frankfurter 作为网络提供者
      let rate: number;
      try {
        rate = await this.fetchFrankfurterRate(from, to);
        console.log(`✅ 使用 frankfurter 获取汇率: ${from} -> ${to} = ${rate}`);
      } catch (frankErr) {
        // frankfurter 失败时，尝试主 ExchangeRate API（仅当配置了 key）
        console.warn('⚠️ frankfurter 获取汇率失败，尝试主 ExchangeRate API（若已配置）:', frankErr);
        const apiKey = this.getExchangeRateApiKey();
        if (apiKey) {
          rate = await this.fetchExchangeRateFromAPI(from, to, apiKey);
          console.log(`✅ 使用主 ExchangeRate API 获取汇率: ${from} -> ${to} = ${rate}`);
        } else {
          // 如果没有主 API key，直接抛出 frankErr 以触发后续缓存降级
          throw frankErr;
        }
      }

      // 成功拿到网络汇率后统一缓存并异步持久化
      const now = Date.now();
      this.rateCache.set(cacheKey, { from, to, rate, timestamp: now });
      this.rateCache.set(`${to}_${from}`, { from: to, to: from, rate: 1 / rate, timestamp: now });
      this.persistCacheToStorage().catch((err) => console.warn('保存汇率缓存到存储失败:', err));

      return rate;
    } catch (error) {
      // 降级到本地缓存（反向缓存、过期缓存或通过 USD 中转）
      console.warn('⚠️ 网络汇率获取失败，尝试使用本地缓存降级:', error);
      try {
        return this.getDefaultRate(from, to);
      } catch (fallbackError) {
        // 降低日志等级，避免被全局错误监听当做用户可见错误展示
        console.warn('缓存降级也失败:', fallbackError);
        console.warn(`🚨 无法获取汇率 ${from} -> ${to}，抛出不支持货币错误`);
        // 抛出特定错误，让调用方知道这是不支持的货币
        const unsupportedError = new Error(`UNSUPPORTED_CURRENCY_PAIR: ${from} -> ${to}`);
        (unsupportedError as any).code = 'UNSUPPORTED_CURRENCY_PAIR';
        throw unsupportedError;
      }
    }
  }

  // 转换金额
  async convertAmount(amount: number, from: string, to: string): Promise<number> {
    const rate = await this.getExchangeRate(from, to);
    return amount * rate;
  }

  // 同步转换金额（仅使用缓存，不调用API）
  convertAmountSync(amount: number, from: string, to: string): number {
    if (from === to) return amount;

    // 检查24小时内的新鲜缓存
    const cacheKey = `${from}_${to}`;
    const cached = this.rateCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`💾 同步转换使用缓存: ${amount} ${from} -> ${to} = ${amount * cached.rate}`);
      return amount * cached.rate;
    }

    // 检查是否有过期但仍可用的缓存
    if (cached) {
      console.log(`⏰ 同步转换使用过期缓存: ${amount} ${from} -> ${to} = ${amount * cached.rate}`);
      return amount * cached.rate;
    }

    // 检查反向汇率缓存
    const reverseCacheKey = `${to}_${from}`;
    const reverseCache = this.rateCache.get(reverseCacheKey);
    if (reverseCache) {
      const rate = 1 / reverseCache.rate;
      console.log(`🔄 同步转换使用反向缓存: ${amount} ${from} -> ${to} = ${amount * rate}`);
      return amount * rate;
    }

    // 如果没有任何缓存，返回原金额并记录警告
    console.warn(`⚠️ 同步转换无缓存数据: ${from} -> ${to}，返回原金额 ${amount}`);
    return amount;
  }

  // 批量转换金额（用于账本列表展示）
  async convertAmountsBatch(
    amounts: Array<{ amount: number; from: string }>,
    targetCurrency: string,
  ): Promise<Array<{ originalAmount: number; convertedAmount: number; rate: number }>> {
    const results = [];

    for (const item of amounts) {
      if (item.from === targetCurrency) {
        results.push({
          originalAmount: item.amount,
          convertedAmount: item.amount,
          rate: 1,
        });
      } else {
        const rate = await this.getExchangeRate(item.from, targetCurrency);
        results.push({
          originalAmount: item.amount,
          convertedAmount: item.amount * rate,
          rate,
        });
      }
    }

    return results;
  }

  // 获取汇率更新时间
  getLastUpdateTime(from: string, to: string): Date | null {
    const cacheKey = `${from}_${to}`;
    const cached = this.rateCache.get(cacheKey);
    return cached ? new Date(cached.timestamp) : null;
  }

  // 清除汇率缓存
  clearCache(): void {
    this.rateCache.clear();
    console.log('🗑️ 汇率缓存已清空');
  }

  // 获取缓存状态
  getCacheStats(): { size: number; rates: Array<{ pair: string; rate: number; age: string }> } {
    const rates = Array.from(this.rateCache.entries()).map(([key, value]) => ({
      pair: key,
      rate: value.rate,
      age: this.formatAge(Date.now() - value.timestamp),
    }));

    return {
      size: this.rateCache.size,
      rates,
    };
  }

  // 格式化时间差
  private formatAge(ms: number): string {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}小时${minutes}分钟前`;
    } else {
      return `${minutes}分钟前`;
    }
  }

  // 预热常用汇率缓存
  async warmupCache(baseCurrency: string = 'CNY'): Promise<void> {
    console.log(`🔥 预热汇率缓存，基准货币: ${baseCurrency}`);

    const targetCurrencies = POPULAR_CURRENCIES.filter((code) => code !== baseCurrency);

    const promises = targetCurrencies.map(async (targetCurrency) => {
      try {
        await this.getExchangeRate(baseCurrency, targetCurrency);
        console.log(`✅ 预热完成: ${baseCurrency} -> ${targetCurrency}`);
      } catch (error) {
        console.error(`❌ 预热失败: ${baseCurrency} -> ${targetCurrency}`, error);
      }
    });

    await Promise.allSettled(promises);
    console.log('🔥 汇率缓存预热完成');
  }

  // 从API获取汇率（真实ExchangeRate-API实现）
  private async fetchExchangeRateFromAPI(from: string, to: string, apiKey: string): Promise<number> {
    console.log(`🌐 调用真实汇率API: ${from} -> ${to}`);

    try {
      if (!apiKey) {
        throw new Error('ExchangeRate API key is not configured. Please set EXCHANGE_RATE_API_KEY in your environment.');
      }
      const url = `https://v6.exchangerate-api.com/v6/${apiKey}/latest/${from}`;
      console.log(`📡 API请求URL: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API请求失败: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data: any = await response.json();
      console.log(`✅ API响应成功 (${from}):`, {
        result: data.result,
        base_code: data.base_code,
        time_last_update_utc: data.time_last_update_utc,
        rates_count: Object.keys(data.conversion_rates || {}).length,
      });

      // ExchangeRate-API v6 使用 conversion_rates 字段
      if (!data.conversion_rates || !data.conversion_rates[to]) {
        throw new Error(`无法获取汇率: ${from} -> ${to}，API响应中缺少目标货币`);
      }

      const rate = data.conversion_rates[to];
      console.log(`💱 获取汇率: ${from} -> ${to} = ${rate}`);

      return rate;
    } catch (error) {
      // 主 API 失败时使用 warn 以减少用户可见性，调用方会根据情况做降级
      console.warn(`❌ API调用失败: ${from} -> ${to}`, error);
      throw error; // 抛出错误，让调用方处理降级逻辑
    }
  }

  // 备用：使用 frankfurter.app 作为降级提供者
  private async fetchFrankfurterRate(from: string, to: string): Promise<number> {
    console.log(`🌐 调用 frankfurter 作为降级汇率提供者: ${from} -> ${to}`);
    // 为了排查设备上为何 frankfurter 未被成功调用，增加更详细的日志并做一次轻量重试
    const fromUC = (from || '').toUpperCase();
    const toUC = (to || '').toUpperCase();
    const url = `https://api.frankfurter.app/latest?from=${encodeURIComponent(fromUC)}&to=${encodeURIComponent(toUC)}`;

    const attemptFetch = async (attempt: number) => {
      try {
        console.log(`📡 frankfurter 请求 (attempt ${attempt}): ${url}`);
        const resp = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
        console.log(`📡 frankfurter 响应状态: ${resp.status} ${resp.statusText}`);
        const text = await resp.text();
        console.log(`📡 frankfurter 响应文本 (truncated 200 chars): ${text.slice(0, 200)}`);
        if (!resp.ok) {
          throw new Error(`frankfurter 请求失败: ${resp.status} ${resp.statusText} - ${text}`);
        }
        let data: any;
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.warn('frankfurter 响应解析 JSON 失败，返回原始文本作为调试信息');
          throw e;
        }

        // frankfurter 的返回 rates 里面的 key 有时是大写或小写，兼容两种情况
        const rateKey = Object.keys(data.rates || {}).find((k) => k.toUpperCase() === toUC);
        let rate: number | null = null;
        if (data && data.rates && rateKey) {
          rate = Number(data.rates[rateKey]);
        } else {
          // 如果没有直接的目标货币汇率，尝试通过 USD 中转（请求 USD -> FROM,TO，然后计算 FROM->TO）
          console.warn(`⚠️ frankfurter 响应不包含 ${toUC}，尝试 USD 中转`);
          const viaUrl = `https://api.frankfurter.app/latest?from=USD&to=${encodeURIComponent(fromUC)},${encodeURIComponent(toUC)}`;
          console.log(`📡 frankfurter USD 中转请求: ${viaUrl}`);
          const viaResp = await fetch(viaUrl, { method: 'GET', headers: { Accept: 'application/json' } });
          const viaText = await viaResp.text();
          let viaData: any;
          try {
            viaData = JSON.parse(viaText);
          } catch (e) {
            console.warn('frankfurter USD 中转响应解析失败:', viaText.slice(0, 200));
            throw new Error(`frankfurter USD 中转响应解析失败`);
          }
          const usdToFrom = viaData.rates && (viaData.rates[fromUC] || viaData.rates[fromUC.toLowerCase()]);
          const usdToTo = viaData.rates && (viaData.rates[toUC] || viaData.rates[toUC.toLowerCase()]);
          if (usdToFrom && usdToTo) {
            // USD -> FROM (usdToFrom), USD -> TO (usdToTo)
            rate = (1 / Number(usdToFrom)) * Number(usdToTo);
            console.log(`🔄 frankfurter 通过 USD 中转计算汇率: ${fromUC} -> ${toUC} = ${rate}`);
          } else {
            throw new Error(`frankfurter 无法通过 USD 中转获取 ${fromUC} 或 ${toUC}`);
          }
        }
        return rate;
      } catch (err) {
        console.warn(`⚠️ frankfurter attempt ${attempt} 失败:`, err);
        throw err;
      }
    };

    // 先尝试一次，如果失败再重试一次（总共最多2次），以应对瞬时网络或 CDN 问题
    try {
      return await attemptFetch(1);
    } catch (err1) {
      try {
        // 1000ms 延迟后重试
        await new Promise((res) => setTimeout(res, 1000));
        return await attemptFetch(2);
      } catch (err2) {
        console.warn('❌ frankfurter 两次尝试均失败，抛出错误以触发上层降级逻辑', err2);
        throw err2;
      }
    }
  }

  // 将当前内存缓存持久化到存储（用于下一次启动时加载）
  private async persistCacheToStorage(): Promise<void> {
    try {
      const arr = Array.from(this.rateCache.values());
      await currencyStorage.setItem(EXCHANGE_RATE_CACHE_KEY, JSON.stringify(arr));
      console.log(`💾 已将 ${arr.length} 条汇率缓存持久化到本地存储`);
    } catch (e) {
      console.warn('持久化汇率缓存失败:', e);
    }
  }

  // 获取降级汇率（仅使用本地缓存，严格禁止硬编码汇率）
  private getDefaultRate(from: string, to: string): number {
    console.log(`⚠️ API调用失败，尝试使用本地缓存降级: ${from} -> ${to}`);

    // 方案1: 检查是否有反向汇率缓存（7天内的缓存可用）
    const reverseCacheKey = `${to}_${from}`;
    const reverseCache = this.rateCache.get(reverseCacheKey);
    if (reverseCache && Date.now() - reverseCache.timestamp < CACHE_DURATION * 7) {
      const rate = 1 / reverseCache.rate;
      console.log(
        `📦 使用反向缓存汇率: ${from} -> ${to} = ${rate} (缓存时间: ${this.formatAge(Date.now() - reverseCache.timestamp)}前)`,
      );
      return rate;
    }

    // 方案2: 检查是否有历史缓存（即使过期，但仍然是真实API数据）
    const cacheKey = `${from}_${to}`;
    const oldCache = this.rateCache.get(cacheKey);
    if (oldCache) {
      console.log(
        `📦 使用过期缓存汇率: ${from} -> ${to} = ${oldCache.rate} (过期时间: ${this.formatAge(Date.now() - oldCache.timestamp)}前)`,
      );
      return oldCache.rate;
    }

    // 方案3: 尝试通过USD中转（如果有USD相关的真实缓存）
    if (from !== 'USD' && to !== 'USD') {
      const fromToUsdCache = this.rateCache.get(`${from}_USD`);
      const usdToTargetCache = this.rateCache.get(`USD_${to}`);

      if (
        fromToUsdCache &&
        usdToTargetCache &&
        Date.now() - fromToUsdCache.timestamp < CACHE_DURATION * 7 &&
        Date.now() - usdToTargetCache.timestamp < CACHE_DURATION * 7
      ) {
        const rate = fromToUsdCache.rate * usdToTargetCache.rate;
        console.log(`🔄 通过USD中转计算汇率: ${from} -> USD(${fromToUsdCache.rate}) -> ${to}(${usdToTargetCache.rate}) = ${rate}`);
        return rate;
      }

      // 尝试使用反向USD中转
      const usdToFromCache = this.rateCache.get(`USD_${from}`);
      const targetToUsdCache = this.rateCache.get(`${to}_USD`);

      if (
        usdToFromCache &&
        targetToUsdCache &&
        Date.now() - usdToFromCache.timestamp < CACHE_DURATION * 7 &&
        Date.now() - targetToUsdCache.timestamp < CACHE_DURATION * 7
      ) {
        const rate = (1 / usdToFromCache.rate) * (1 / targetToUsdCache.rate);
        console.log(`🔄 通过反向USD中转计算汇率: ${from} -> USD -> ${to} = ${rate}`);
        return rate;
      }
    }

    // 如果所有降级方案都失败，记录为 warn 并抛出
    console.warn(`🚨 所有降级方案失败，无法获取汇率: ${from} -> ${to}`);
    console.warn(
      `缓存状态: 直接缓存=${!!this.rateCache.get(`${from}_${to}`)}, 反向缓存=${!!this.rateCache.get(`${to}_${from}`)}`,
    );
    throw new Error(`无法获取汇率数据: ${from} -> ${to}。请检查网络连接后重试，或联系客服。`);
  }
}

export default CurrencyService;
